import * as z from "zod";
import { userFacingError } from "../authorization";
import { dcrServerChanged, withoutDiscovery } from "../connectors/dcr";
import { canAuthorizeVisitors } from "../connectors/isDraft";
import { connectorDestinationAuditMetadata } from "../connectors/auditDestination";
import { normalizedConnectorIdentity } from "../connectors/normalizeUrlPattern";
import { mutator } from "./mutator";
import { disconnectOAuthConnection } from "./resetOAuthConnection";
import { stringRecord } from "./utils";

export const updateConnectorSchema = z.object({
  connectedAppId: z.string(),
  name: z.string(),
  identifier: z.string(),
  urlPattern: z.string(),
  configuration: stringRecord,
  secrets: stringRecord.optional(),
  oauthConnectionId: z.string().optional(),
  note: z.string().optional(),
});

export const updateConnectedApp = mutator({
  redactKeys: ["secrets"],
  schema: updateConnectorSchema,
  resolve: {
    app: { kind: "connectedApp", from: "connectedAppId" },
  },
  permissions: [
    {
      permission: "connected_app:write",
      scopeId: ({ args }) => args.app.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      app,
      name,
      identifier,
      urlPattern,
      configuration,
      oauthConnectionId,
      note,
    },
  }) => {
    if (oauthConnectionId !== undefined)
      throw userFacingError(
        "Connectors can’t reuse an existing OAuth credential"
      );
    if (
      app.authorizationMode === "visitor" &&
      !canAuthorizeVisitors({ ...app, identifier, configuration })
    ) {
      throw userFacingError(
        "Switch to owner authorization before changing how this connector authenticates"
      );
    }
    const identity = normalizedConnectorIdentity({
      name,
      identifier,
      urlPattern,
      configuration,
      previousConfiguration: app.configuration,
    });
    const serverChanged = dcrServerChanged(app, identity);
    await tx.mutate.connectedApps.update({
      id: app.id,
      ...identity,
      ...(note !== undefined && { note }),
      ...(serverChanged && {
        configuration: withoutDiscovery(identity.configuration),
        oauthClientId: "",
        hasOAuthClientSecret: false,
      }),
    });
    if (serverChanged && app.oauthConnectionId) {
      await disconnectOAuthConnection(tx, app.oauthConnectionId);
    }
    ctx.recordAuditMetadata?.(connectorDestinationAuditMetadata(app, identity));
  },
});
